import { setMilliseconds, sub } from 'date-fns';
import { create, StoreApi, UseBoundStore } from 'zustand';

import { SortingState } from '@tanstack/react-table';

import FilterValueArray from 'models/filterValues/FilterValueArray';

import { serializeSort } from './helpers';
import { FilterState } from './types';

export { serializeSort };

export type AdmissionsFilterState = FilterState<FilterValueArray>;

export type DateRange = { startDate: Date; endDate: Date };

const defaultDateRange = (): DateRange => {
  const now = setMilliseconds(new Date(), 0);
  return { startDate: sub(now, { days: 30 }), endDate: now };
};

type AdmissionsActions = {
  setFilters: (filters: AdmissionsFilterState | ((prev: AdmissionsFilterState) => AdmissionsFilterState)) => void;
  setSort: (sort: SortingState) => void;
  setSearch: (search: string) => void;
  setDateRange: (dateRange: DateRange | null) => void;
  removeFilter: (filterKey: string, id: string) => void;
  setExpanded: (expanded: boolean) => void;
};

export type AdmissionsState = {
  filters: AdmissionsFilterState;
  sort: SortingState;
  search: string;
  dateRange: DateRange | null;
  actions: AdmissionsActions;
  expanded: boolean;
};

const createAdmissionsStore = (): [
  useStore: UseBoundStore<StoreApi<AdmissionsState>>,
  useActions: () => AdmissionsActions,
] => {
  const useStore = create<AdmissionsState>((set) => ({
    filters: {},
    sort: [{ id: 'updatedAt', desc: true }],
    search: '',
    dateRange: defaultDateRange(),
    expanded: false,
    actions: {
      setFilters: (filters) =>
        set((state) => ({
          filters: typeof filters === 'function' ? filters(state.filters) : filters,
        })),
      setSort: (sort) => set({ sort }),
      setSearch: (search) => set({ search }),
      setDateRange: (dateRange) => set({ dateRange }),
      removeFilter: (filterKey, id) =>
        set((state) => ({
          filters: {
            ...state.filters,
            ...(state.filters[filterKey] ? { [filterKey]: state.filters[filterKey].removeFilter(id) } : {}),
          },
        })),
      setExpanded: (expanded) => set({ expanded }),
    },
  }));

  const useActions = () => useStore((state) => state.actions);

  return [useStore, useActions];
};

export const [useAdmissionsStore, useAdmissionsActions] = createAdmissionsStore();
